import React, {
  memo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import { useMutation, useQueryClient } from 'react-query';
import {
  archiveInventoryCount,
  manualRefreshTriggerById,
  updateInventoryCount,
} from 'api/inventory';

import {
  EditRefType,
  EditInventoryCountModalProps,
  EditInventoryCountFormikProps,
} from './EditInventoryCountModal.props';
import { InventoryCount } from 'types/inventory';

import EditInventoryCountModalView from './EditInventoryCountModal.view';
import { AlertRefType } from 'components/primitives/Alert';
import { FormikProps } from 'formik';

const EditInventoryCountModalContainer = forwardRef<
  EditRefType,
  EditInventoryCountModalProps
>((props, ref) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [count, setCount] = useState<InventoryCount | null>(null);

  const [toggleSwitch, setToggleSwitch] = useState<boolean | null>(null);

  const archiveAlertRef = useRef<AlertRefType>(null);
  const saveAlertRef = useRef<AlertRefType>(null);
  const formikRef = useRef<FormikProps<EditInventoryCountFormikProps>>(null);

  const { mutate, isLoading } = useMutation(updateInventoryCount, {
    onSuccess: () => {
      toast({
        status: 'success',
        isClosable: true,
        description: 'Inventory count updated',
      });

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('fetch-inventory-counts'), //This will invalidate all queries that have 'fetch-inventory-counts' anywhere in their query key
      });
      onClose();
      setCount(null);
    },
  });

  const {
    mutate: mutateToggleArchive,
    isLoading: archiveLoading,
  } = useMutation(archiveInventoryCount, {
    onSuccess: () => {
      toast({
        status: 'success',
        isClosable: true,
        description: 'Inventory count updated',
      });

      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('fetch-inventory-counts'), //This will invalidate all queries that have 'fetch-inventory-counts' anywhere in their query key
      });

      onClose();
      setCount(null);
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_count) => {
        setCount(_count);
        onOpen();
      },
    }),
    [],
  );

  useEffect(() => {
    if (isOpen) {
      setToggleSwitch(count?.isArchived ?? null);
    }
  }, [isOpen]);

  const onSubmit = (
    payload: Pick<
      InventoryCount,
      'openItems' | 'openCases' | 'sealedPackages' | 'notes' | 'countDate'
    >,
  ) => {
    if (!count) {
      return;
    }

    payload.openItems.forEach((i) => {
      i.quantity = Number(i.quantity || '0');
      i.sealedQuantity = Number(i.sealedQuantity || '0');
    });
    payload.openCases.forEach((i) => {
      i.quantity = Number(i.quantity || '0');
      i.sealedQuantity = Number(i.sealedQuantity || '0');
    });
    payload.sealedPackages.forEach((i) => {
      i.quantity = Number(i.quantity || '0');
      i.sealedQuantity = Number(i.sealedQuantity || '0');
    });

    mutate({
      id: count._id,
      ...payload,
    });
  };

  const switchOnChange = (value: boolean) => {
    if (!count) {
      return;
    }
    if (value && count.harvestCount > 0) archiveAlertRef.current?.onOpen?.();
    else {
      let id = count._id;
      mutateToggleArchive(id);
    }
    setToggleSwitch((prevState) => !prevState ?? true);
  };

  const cancelArchiveAlert = () => {
    setToggleSwitch((prevState) => !prevState ?? true);
  };
  const proceedArchiveAlert = () => {
    if (!count) {
      return;
    }
    let id = count._id;
    mutateToggleArchive(id);
  };
  const openSaveAlert = () => {
    saveAlertRef.current?.onOpen?.();
  };
  const cancelSaveAlert = () => {
    saveAlertRef.current?.onClose?.();
  };
  const proceedSaveAlert = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  const onRefresh = async () => {
    if (!count?._id) return 'No id';
    let response = await manualRefreshTriggerById(count._id);
    if (response) {
      setCount(response.item);
    }
  };
  return (
    <EditInventoryCountModalView
      count={count}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      onRefresh={onRefresh}
      isLoading={isLoading || archiveLoading}
      switchOnChange={switchOnChange}
      archiveAlertRef={archiveAlertRef}
      cancelArchiveAlert={cancelArchiveAlert}
      proceedArchiveAlert={proceedArchiveAlert}
      saveAlertRef={saveAlertRef}
      cancelSaveAlert={cancelSaveAlert}
      proceedSaveAlert={proceedSaveAlert}
      openSaveAlert={openSaveAlert}
      toggleSwitch={toggleSwitch}
      formikRef={formikRef}
    />
  );
});

const EditInventoryCountModal = memo(EditInventoryCountModalContainer);

export default EditInventoryCountModal;
